import {
  ReferralClient,
  LeaderboardClient,
  ExchangeClient,
  CryptoAlertsClient,
  StockNewsClient,
  PositionClient,
  MarketDataClient,
  NewsSubscriptionClient,
  TokenAnalysisClient,
  TokenMetadataClient,
  TriggerClient,
  CopyTradeClient,
  ApiClientInterceptors,
} from '@crypto-trading/common/browser';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  'https://main-api-production-1bb6.up.railway.app/api';

/**
 * JWT token storage and retrieval
 */
export class TokenManager {
  private static readonly TOKEN_KEY = 'auth_token';

  static getToken(): string {
    return localStorage.getItem(this.TOKEN_KEY) || '';
  }

  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  static clearToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }
}

/**
 * Singleton instances of API clients
 */
class ApiClientManager {
  private static referralClient: ReferralClient | null = null;
  private static leaderboardClient: LeaderboardClient | null = null;
  private static exchangeClient: ExchangeClient | null = null;
  private static cryptoAlertsClient: CryptoAlertsClient | null = null;
  private static stockNewsClient: StockNewsClient | null = null;
  private static positionClient: PositionClient | null = null;
  private static marketDataClient: MarketDataClient | null = null;
  private static newsSubscriptionClient: NewsSubscriptionClient | null = null;
  private static tokenAnalysisClient: TokenAnalysisClient | null = null;
  private static tokenMetadataClient: TokenMetadataClient | null = null;
  private static triggerClient: TriggerClient | null = null;
  private static copyTradeClient: CopyTradeClient | null = null;
  private static interceptors: ApiClientInterceptors | null = null;

  /**
   * Set interceptors for all API clients (must be called before getting clients)
   */
  static setInterceptors(interceptors: ApiClientInterceptors): void {
    this.interceptors = interceptors;
    // Reset clients so they'll be recreated with new interceptors
    this.referralClient = null;
    this.leaderboardClient = null;
    this.exchangeClient = null;
    this.cryptoAlertsClient = null;
    this.stockNewsClient = null;
    this.positionClient = null;
    this.marketDataClient = null;
    this.newsSubscriptionClient = null;
    this.tokenAnalysisClient = null;
    this.tokenMetadataClient = null;
    this.triggerClient = null;
    this.copyTradeClient = null;
    console.log('[ApiClientManager] Interceptors updated, clients will be recreated');
  }

  /**
   * Get or create ReferralClient instance
   */
  static getReferralClient(): ReferralClient {
    if (!this.referralClient) {
      this.referralClient = new ReferralClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new ReferralClient instance');
    }
    return this.referralClient;
  }

  /**
   * Get or create LeaderboardClient instance
   */
  static getLeaderboardClient(): LeaderboardClient {
    if (!this.leaderboardClient) {
      this.leaderboardClient = new LeaderboardClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new LeaderboardClient instance');
    }
    return this.leaderboardClient;
  }

  /**
   * Get or create ExchangeClient instance
   */
  static getExchangeClient(): ExchangeClient {
    if (!this.exchangeClient) {
      this.exchangeClient = new ExchangeClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new ExchangeClient instance');
    }
    return this.exchangeClient;
  }

  /**
   * Get or create CryptoAlertsClient instance
   */
  static getCryptoAlertsClient(): CryptoAlertsClient {
    if (!this.cryptoAlertsClient) {
      this.cryptoAlertsClient = new CryptoAlertsClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new CryptoAlertsClient instance');
    }
    return this.cryptoAlertsClient;
  }

  static getStockNewsClient(): StockNewsClient {
    if (!this.stockNewsClient) {
      this.stockNewsClient = new StockNewsClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
    }
    return this.stockNewsClient;
  }

  /**
   * Get or create PositionClient instance
   */
  static getPositionClient(): PositionClient {
    if (!this.positionClient) {
      this.positionClient = new PositionClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new PositionClient instance');
    }
    return this.positionClient;
  }

  /**
   * Get or create MarketDataClient instance
   */
  static getMarketDataClient(): MarketDataClient {
    if (!this.marketDataClient) {
      this.marketDataClient = new MarketDataClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new MarketDataClient instance');
    }
    return this.marketDataClient;
  }

  /**
   * Get or create NewsSubscriptionClient instance
   */
  static getNewsSubscriptionClient(): NewsSubscriptionClient {
    if (!this.newsSubscriptionClient) {
      this.newsSubscriptionClient = new NewsSubscriptionClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new NewsSubscriptionClient instance');
    }
    return this.newsSubscriptionClient;
  }

  /**
   * Get or create TokenAnalysisClient instance
   */
  static getTokenAnalysisClient(): TokenAnalysisClient {
    if (!this.tokenAnalysisClient) {
      this.tokenAnalysisClient = new TokenAnalysisClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new TokenAnalysisClient instance');
    }
    return this.tokenAnalysisClient;
  }

  /**
   * Get or create TokenMetadataClient instance
   */
  static getTokenMetadataClient(): TokenMetadataClient {
    if (!this.tokenMetadataClient) {
      this.tokenMetadataClient = new TokenMetadataClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new TokenMetadataClient instance');
    }
    return this.tokenMetadataClient;
  }

  /**
   * Get or create TriggerClient instance
   */
  static getTriggerClient(): TriggerClient {
    if (!this.triggerClient) {
      this.triggerClient = new TriggerClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
      console.log('[ApiClientManager] Created new TriggerClient instance');
    }
    return this.triggerClient;
  }

  static getCopyTradeClient(): CopyTradeClient {
    if (!this.copyTradeClient) {
      this.copyTradeClient = new CopyTradeClient({
        baseURL: API_BASE_URL,
        authType: 'jwt',
        getJwtToken: () => TokenManager.getToken(),
        timeout: 100000,
        interceptors: this.interceptors || undefined,
      });
    }
    return this.copyTradeClient;
  }
}

export { ApiClientManager };

